import { z } from 'zod'

/** A money value carries its own currency, or is explicitly undisclosed. */
export const moneyValueSchema = z.object({
  amount: z.number().nullable().optional(),
  currency: z.string().max(8).optional(),
  undisclosed: z.boolean().optional(),
})
export type MoneyValue = z.infer<typeof moneyValueSchema>

/** Dates may be captured at day, month or year precision (spec Appendix C). */
export const dateValueSchema = z.object({
  value: z.string().regex(/^\d{4}(-\d{2}(-\d{2})?)?$/),
  precision: z.enum(['day', 'month', 'year']).default('day'),
})
export type DateValue = z.infer<typeof dateValueSchema>

export const partySchema = z.object({
  name: z.string().min(1).max(200),
  role: z.string().max(60).optional(),
})

export const rowSchema = z.object({
  id: z.string().uuid(),
  values: z.record(z.unknown()),
  /** Confirmed unchanged | Edited | New — set relative to a pre-loaded row. */
  confirmationState: z.enum(['new', 'confirmed', 'edited']).default('new'),
  preloadedRowId: z.string().uuid().nullable().optional(),
  /**
   * The Workbench record id of this row's pre-loaded source, copied from `PreloadedRow.coreRecordId`
   * onto the row itself. `preloadedRowId` names a `PreloadedRow` that a re-import can delete and
   * recreate with a new id; this id survives that, so a submission event can still say which
   * Workbench record a kept row belongs to, and which offered records went unanswered.
   */
  sourceRecordId: z.string().nullable().optional(),
  /**
   * Set on a row Stears itself supplied from its own records rather than a CSV pre-load —
   * the company's contact list, for instance. Shown as "Provided by Stears, please confirm"
   * alongside pre-loaded rows (FRM-09).
   */
  providedByStears: z.boolean().optional(),
  confidential: z.boolean().optional(),
})
export type DraftRow = z.infer<typeof rowSchema>

export const tabStateSchema = z.object({
  values: z.record(z.unknown()).default({}),
  rows: z.record(z.array(rowSchema)).default({}),
  controls: z.record(z.unknown()).default({}),
  flags: z.record(z.boolean()).default({}),
  note: z.string().max(4000).default(''),
})
export type TabState = z.infer<typeof tabStateSchema>

export const draftPayloadSchema = z.object({
  tabs: z.record(tabStateSchema).default({}),
})
export type DraftPayload = z.infer<typeof draftPayloadSchema>

export function emptyTabState(): TabState {
  return { values: {}, rows: {}, controls: {}, flags: {}, note: '' }
}
export function emptyDraftPayload(): DraftPayload {
  return { tabs: {} }
}

/* ------------------------------------------------------------------ patch ops */

export const patchOperationSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('set'), tab: z.string(), section: z.string(), field: z.string(), value: z.unknown() }),
  z.object({ op: z.literal('unset'), tab: z.string(), section: z.string(), field: z.string() }),
  z.object({ op: z.literal('rowUpsert'), tab: z.string(), section: z.string(), row: rowSchema }),
  z.object({ op: z.literal('rowRemove'), tab: z.string(), section: z.string(), rowId: z.string().uuid(), reason: z.string().max(500).optional() }),
  z.object({ op: z.literal('control'), tab: z.string(), key: z.string(), value: z.unknown() }),
  z.object({ op: z.literal('flag'), tab: z.string(), key: z.string(), value: z.boolean() }),
  z.object({ op: z.literal('note'), tab: z.string(), value: z.string().max(4000) }),
])
export type PatchOperation = z.infer<typeof patchOperationSchema>

export const patchRequestSchema = z.object({
  baseRevision: z.number().int().nonnegative(),
  operations: z.array(patchOperationSchema).min(1).max(200),
})
export type PatchRequest = z.infer<typeof patchRequestSchema>

/** Applies operations to a payload in place and returns the tabs that changed. */
export function applyOperations(payload: DraftPayload, ops: PatchOperation[]): Set<string> {
  const touched = new Set<string>()
  for (const op of ops) {
    const tab = (payload.tabs[op.tab] ??= emptyTabState())
    touched.add(op.tab)
    switch (op.op) {
      case 'set':
        tab.values[`${op.section}.${op.field}`] = op.value
        break
      case 'unset':
        delete tab.values[`${op.section}.${op.field}`]
        break
      case 'rowUpsert': {
        const rows = (tab.rows[op.section] ??= [])
        const idx = rows.findIndex((r) => r.id === op.row.id)
        if (idx >= 0) rows[idx] = op.row
        else rows.push(op.row)
        break
      }
      case 'rowRemove': {
        const rows = tab.rows[op.section] ?? []
        tab.rows[op.section] = rows.filter((r) => r.id !== op.rowId)
        if (op.reason) {
          const stamp = `Removed row: ${op.reason}`
          tab.note = tab.note ? `${tab.note}\n${stamp}` : stamp
        }
        break
      }
      case 'control':
        tab.controls[op.key] = op.value
        break
      case 'flag':
        tab.flags[op.key] = op.value
        break
      case 'note':
        tab.note = op.value
        break
    }
  }
  return touched
}
